import type {
  Location,
  MyWeatherSyncPlace,
  PlaceIdDetail,
  SavedPlace,
} from '../../api/types';
import {buildPlaceLabel, formatPlaceTitle, hasPlaceLabel} from '../Places';
import {USER_LOCATION_HASH} from './constants';

/**
 * The `MyWeather` namespace: how a device's places and order map onto the
 * account's (backend#266).
 *
 * On the device a place stays identified by its `hash`, which screens, the
 * per-place forecast model and navigation already key on: the Google place
 * id, as the picker has always stored it. The account names the same place
 * by a UUID (`serverId`). The order is held with hashes and translated at
 * the wire.
 */

/**
 * Whether this device keeps its places in step with the account: the
 * session flag `my_weather_sync`, read from the /me body that carries it
 * (at sign-in the store does not hold it yet) or from the store.
 */
const SYNC_FLAG = 'my_weather_sync';

export const isSyncEnabled = (
  featureFlags: Record<string, boolean> | undefined,
): boolean => featureFlags?.[SYNC_FLAG] === true;

export const PLACE_KIND = 'place';
export const STATION_KIND = 'station';

// Mirrors backend apps/user/places/utils.py, so the server never refuses an
// order this module produced: one bad token fails the whole PATCH. Python's
// \S, not JS's: the two disagree on U+001C-001F, U+0085 and U+FEFF. NUL and
// lone surrogates are out too, because DRF's CharField refuses them.
const ORDER_TOKEN_RE =
  // eslint-disable-next-line no-control-regex -- the control characters are the point: they are Python whitespace
  /^[a-z][a-z_]{0,31}:[^\t\n\v\f\r\x1c-\x20\x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\0\ud800-\udfff]{1,128}$/u;
const STATION_ID_RE = /^[0-9]{1,10}$/;
const INT32_MAX = 2 ** 31 - 1;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// MY_WEATHER_ORDER_MAX on the server.
const ORDER_MAX = 5000;

// The server's column widths, in characters (SavedPlace.label, subtitle,
// place_id; the sync's client_id).
const TEXT_MAX = 255;

/** Characters, not UTF-16 units, so an emoji is never cut in half. */
const characters = (text: string): string[] => Array.from(text);

const truncate = (text: string): string =>
  characters(text).slice(0, TEXT_MAX).join('');

export type OrderToken = {kind: string; id: string};

/**
 * A coordinate rounded half-up to 3 decimals, as the backend's dedup key
 * rounds it (`Decimal(repr(v))`, ROUND_HALF_UP).
 *
 * Done on the shortest decimal string rather than with `toFixed`, which
 * rounds the binary float: 48.8565 is stored as 48.85649999..., so `toFixed`
 * gives 48.856 where the server, like a person, reads 48.857.
 */
const roundCoordinate = (value: number): string => {
  // Below 1e-6 String() switches to exponent notation; all of it rounds to 0.
  if (Math.abs(value) < 1e-6) {
    return '0.000';
  }
  const negative = value < 0;
  const [whole, fraction = ''] = String(Math.abs(value)).split('.');
  const digits = (fraction + '000').slice(0, 3);
  let thousandths = Number(whole) * 1000 + Number(digits);
  if (fraction.charAt(3) >= '5') {
    thousandths += 1; // half-up: away from zero
  }
  if (thousandths === 0) {
    return '0.000'; // never "-0.000"
  }
  const text = (thousandths / 1000).toFixed(3);
  return negative ? `-${text}` : text;
};

/** Whether a hash names a point (`pt:…`) rather than a Google place. */
export const isPointHash = (hash: string): boolean => hash.startsWith('pt:');

/** A point as the backend dedup key writes it: `pt:<lat>,<lon>`, 3 dp. */
export const pointHash = (latitude: number, longitude: number): string =>
  `pt:${roundCoordinate(latitude)},${roundCoordinate(longitude)}`;

/** The name of a place known only by its point: "48.842, 2.296". */
const pointLabel = (latitude: number, longitude: number): string =>
  `${roundCoordinate(latitude)}, ${roundCoordinate(longitude)}`;

/**
 * The device hash of a saved place: its Google place id, else the point as
 * the backend dedup key writes it. Two devices thus name a place the same.
 */
export const placeHash = (
  place: Pick<SavedPlace, 'place_id' | 'latitude' | 'longitude'>,
): string => place.place_id || pointHash(place.latitude, place.longitude);

/**
 * Where the GPS entry is now, as a place that stays put (FE #388): what the
 * star on the current-position dashboard saves.
 *
 * Named by the reverse-geocoded place, else by its coordinates, never by
 * "Current location", the role of the entry that follows the user. A point
 * has no place id, so it syncs as one (backend#266). No altitude: the
 * account stores none, and the first /me replaces these coordinates.
 */
export const pointLocationFromUserLocation = (gps: Location): Location => {
  const {latitude, longitude} = gps.coordinates;
  const label = buildPlaceLabel(gps.place);
  const named = hasPlaceLabel(label);
  return {
    hash: pointHash(latitude, longitude),
    coordinates: {latitude, longitude},
    title: named ? formatPlaceTitle(label) : pointLabel(latitude, longitude),
    ...(named && label.subtitle ? {subtitle: label.subtitle} : {}),
    timezone: gps.timezone,
  };
};

/** A position fix, as the browser and the device's geolocation give one. */
export type PositionFix = {
  latitude: number;
  longitude: number;
  altitude?: number | null;
};

/**
 * The GPS entry for a new fix, in both apps. Rounded to 3 decimals
 * (~110 m): `useLocationForecast` compares the entry's coordinates exactly,
 * so every fix must round alike. The name is left empty on purpose:
 * `useUserLocationPlace` resolves it, and an empty one with no `place` is
 * what marks the coordinates as needing a lookup.
 */
export const userLocationFromFix = (
  fix: PositionFix,
  timezone: string,
): Location => ({
  hash: USER_LOCATION_HASH,
  coordinates: {
    latitude: +fix.latitude.toFixed(3),
    longitude: +fix.longitude.toFixed(3),
    ...(fix.altitude != null ? {altitude: fix.altitude} : {}),
  },
  title: '',
  place: undefined,
  placeLocale: undefined,
  timezone,
});

/**
 * A place known by its Google id alone (a shared `/app/places/<id>` link,
 * FE #381 W4), from the details the backend proxy answers: named as a pick
 * would be (its name, the rest of the address under it), else by its
 * address, else by its point.
 */
export const placeFromDetail = (
  placeId: string,
  detail: PlaceIdDetail,
): Location => {
  const {lat: latitude, lng: longitude} = detail.geometry.location;
  const address = detail.formatted_address?.trim() || '';
  const name = detail.name?.trim() || address.split(',')[0].trim();
  const prefix = `${name}, `;
  const subtitle = address.startsWith(prefix)
    ? address.slice(prefix.length)
    : address !== name
      ? address
      : '';
  return {
    hash: placeId,
    coordinates: {latitude, longitude},
    title: name || pointLabel(latitude, longitude),
    ...(subtitle ? {subtitle} : {}),
    timezone: detail.timezone as string,
  };
};

/**
 * What a place's star saves, in both apps: the place itself, or for the GPS
 * entry, which follows the user, where they are now as a fixed point
 * (FE #388).
 */
export const favoriteTarget = (location: Location): Location =>
  location.hash === USER_LOCATION_HASH
    ? pointLocationFromUserLocation(location)
    : location;

export const locationFromSavedPlace = (place: SavedPlace): Location => ({
  hash: placeHash(place),
  serverId: place.id,
  coordinates: {latitude: place.latitude, longitude: place.longitude},
  title: place.label,
  ...(place.subtitle ? {subtitle: place.subtitle} : {}),
  timezone: place.timezone,
});

const inRange = (value: number, limit: number): boolean =>
  Number.isFinite(value) && Math.abs(value) <= limit;

/**
 * A stored location as the sync uploads it, or null for one that must not
 * or cannot go: the GPS entry, and a place the server would refuse (which
 * would fail the whole batch, at every launch). A refused place stays on
 * the device only.
 */
export const toSyncPlace = (location: Location): MyWeatherSyncPlace | null => {
  const {hash, coordinates, title, subtitle} = location;
  const {latitude, longitude} = coordinates;
  if (hash === USER_LOCATION_HASH) {
    return null;
  }
  if (!inRange(latitude, 90) || !inRange(longitude, 180)) {
    return null;
  }
  if (characters(hash).length > TEXT_MAX) {
    return null;
  }
  const label = title.trim() || pointLabel(latitude, longitude);
  return {
    client_id: hash,
    label: truncate(label),
    subtitle: subtitle ? truncate(subtitle) : null,
    place_id: isPointHash(hash) ? null : hash,
    latitude,
    longitude,
  };
};

export const stationToken = (stationId: number): string =>
  `${STATION_KIND}:${stationId}`;

export const placeToken = (hash: string): string => `${PLACE_KIND}:${hash}`;

/** A token split at its first colon, or null if the server would refuse it. */
export const parseToken = (token: string): OrderToken | null => {
  if (!ORDER_TOKEN_RE.test(token)) {
    return null;
  }
  const separator = token.indexOf(':');
  const kind = token.slice(0, separator);
  const id = token.slice(separator + 1);
  if (
    kind === STATION_KIND &&
    (!STATION_ID_RE.test(id) || Number(id) > INT32_MAX)
  ) {
    return null;
  }
  return {kind, id};
};

const dedupe = (tokens: string[]): string[] => [...new Set(tokens)];

const PLACE_PREFIX = `${PLACE_KIND}:`;

/**
 * The device order as the account stores it: place hashes become server
 * ids. Places not on the account yet are left out (the sync sends them);
 * so is the GPS entry. A server id naming no local place was saved on
 * another device and is kept, so a save here does not drop it there.
 */
export const toServerOrder = (
  order: string[],
  places: Pick<Location, 'hash' | 'serverId'>[],
): string[] => {
  const byHash = new Map(places.map((place) => [place.hash, place]));
  const tokens: string[] = [];
  for (const token of order) {
    // Translate before validating: a hash may be too long for a token
    // while its server id is not.
    const place = token.startsWith(PLACE_PREFIX)
      ? byHash.get(token.slice(PLACE_PREFIX.length))
      : undefined;
    const sent = place?.serverId ? placeToken(place.serverId) : token;
    const parsed = place && !place.serverId ? null : parseToken(sent);
    if (!parsed) {
      continue;
    }
    if (parsed.kind === PLACE_KIND && !UUID_RE.test(parsed.id)) {
      continue; // a hash: the GPS entry, or a place gone from this device
    }
    tokens.push(sent);
  }
  return dedupe(tokens).slice(0, ORDER_MAX);
};

/**
 * A group of rows the user reordered, written back into the My weather
 * order (FE #381): the group takes the slots its tokens already hold, in
 * its new order, and what the order lacked follows it. Every other token
 * keeps its slot: another device's places, owned stations, kinds a later
 * app adds. So reordering the stations on one screen leaves the places
 * between them where they were.
 */
export const reorder = (order: string[] | null, keys: string[]): string[] => {
  const group = dedupe(keys);
  if (!order) {
    return group;
  }
  const inGroup = new Set(group);
  let next = 0;
  const tokens = dedupe(order).map((token) =>
    inGroup.has(token) ? group[next++] : token,
  );
  return dedupe([...tokens, ...group.slice(next)]);
};

/**
 * The My weather order once the station settings page (web: Modify favorite
 * stations) has reordered the stations: the favourite stations move there
 * too, the places between them keep their slots. Null when there is no
 * order yet (the station order is the order then) or nothing in it moved.
 * Own stations (linked) are left out: My stations follows the station order.
 */
export const orderAfterStationMove = (
  order: string[] | null,
  stations: ReadonlyArray<{id: number; link?: unknown}>,
  favoriteIds: ReadonlyArray<number>,
): string[] | null => {
  if (!order) {
    return null;
  }
  const favorites = stations
    .filter((station) => !station.link && favoriteIds.includes(station.id))
    .map((station) => stationToken(station.id));
  const next = reorder(order, favorites);
  const same =
    next.length === order.length &&
    next.every((token, index) => token === order[index]);
  return same ? null : next;
};

/**
 * The account order with server ids turned back into device hashes.
 *
 * It holds neither the GPS entry nor places not synced yet: merge it into
 * the device order rather than replacing that with it.
 */
export const fromServerOrder = (
  order: string[] | null | undefined,
  places: SavedPlace[],
): string[] | null => {
  if (!order) {
    return null;
  }
  const hashById = new Map(places.map((place) => [place.id, placeHash(place)]));
  return order.map((token) => {
    const parsed = parseToken(token);
    const hash =
      parsed?.kind === PLACE_KIND ? hashById.get(parsed.id) : undefined;
    return hash ? placeToken(hash) : token;
  });
};

export {USER_LOCATION_HASH} from './constants';
export {buildEditorSections, buildSections, sectionPlaces} from './sections';
export {editorStationIds, moveKey} from './editor';
export type {
  PlaceResults,
  SearchRow,
  SearchSourceName,
  SearchStatus,
  StationResults,
} from './search';
export type {
  MyWeatherEditorInput,
  MyWeatherEditorSections,
  MyWeatherItem,
  MyWeatherPlaceItem,
  MyWeatherSection,
  MyWeatherSections,
  MyWeatherSectionsInput,
  MyWeatherStationItem,
} from './sections';
export {placeNowView} from './now';
export type {
  NowErrorReason,
  NowModel,
  NowPlace,
  NowRain,
  NowRequest,
  NowRequestItem,
  NowState,
  NowTemperature,
  NowWind,
  PlaceNowContext,
  PlaceNowView,
} from './now';
export {
  SEARCH_MIN_LENGTH,
  STATIONS_SHOWN,
  editOfLabel,
  placeFromPick,
  searchFailedMessage,
  searchQuery,
  searchRows,
  searchSummary,
  stationNearbyFilter,
  stationSearchFilter,
} from './search';
